// Lecture de l'adresse contenue dans `operations.xml_macdo` (commande bois
// envoyée par la borne ScreenWoodOrder).
//
// Les factures firewood importées AVANT que transformToInvoice ne persiste
// l'adresse n'ont pas de idaddressbilling/idaddressdelivery : l'adresse ne vit
// que dans le XML. L'écran VUE savait la relire, l'écran ÉDITER non — d'où un
// champ adresse vide alors que la facture « affichait » une adresse ailleurs.
//
// Extrait de views/invoicing/FirewoodInvoiceView.vue pour être partagé par les
// deux écrans, avec en plus `loadXmlMacdoAddressHints()` qui délègue
// l'interprétation de la ligne à l'API (POST /api/address/parse-ro) : l'écran
// affiche ainsi EXACTEMENT ce que l'import aurait créé, au lieu d'une seconde
// heuristique qui divergerait de celle du serveur.
import axios from '@/axios.js'

export default {
  methods: {
    /** Extrait les deux lignes brutes <Billing> / <Delivery> du XML WoodOrder. */
    extractAddressTextsFromXmlMacdo (xmlString) {
      const out = { billing: '', delivery: '' }
      if (!xmlString || typeof xmlString !== 'string') return out
      const raw = String(xmlString).replace(/\\n/g, '\n').trim()
      if (!raw) return out
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(raw, 'text/xml')
        const root = doc.documentElement
        if (root) {
          const addressesEl = root.querySelector('Addresses') || root.getElementsByTagName('Addresses')[0]
          if (addressesEl) {
            const billingEl = addressesEl.querySelector('Billing') || addressesEl.getElementsByTagName('Billing')[0]
            const deliveryEl = addressesEl.querySelector('Delivery') || addressesEl.getElementsByTagName('Delivery')[0]
            out.billing = billingEl && billingEl.textContent ? String(billingEl.textContent).trim() : ''
            out.delivery = deliveryEl && deliveryEl.textContent ? String(deliveryEl.textContent).trim() : ''
          }
        }
      } catch (e) {
        console.warn('extractAddressTextsFromXmlMacdo DOMParser:', e)
      }
      // Repli regex : certains XML arrivent mal formés (échappements de la borne).
      // `[^>]*` obligatoire : depuis la saisie structurée, <Billing> porte des
      // attributs (placeId, postalCode…) et un motif `<Billing>` littéral ne
      // matcherait plus rien — le repli se serait tu au lieu de dépanner.
      if (!out.billing && !out.delivery) {
        const mB = raw.match(/<Billing[^>]*>([^<]*)<\/Billing>/)
        const mD = raw.match(/<Delivery[^>]*>([^<]*)<\/Delivery>/)
        if (mB) out.billing = mB[1].trim()
        if (mD) out.delivery = mD[1].trim()
      }
      return out
    },

    /**
     * Champs séparés portés par les attributs de <Billing>/<Delivery>.
     * Miroir front de addressStructFromNode (woodOrderQueue.controller.js).
     * Chaque côté vaut null si la commande a été saisie en texte libre.
     */
    extractAddressStructsFromXmlMacdo (xmlString) {
      const out = { billing: null, delivery: null }
      if (!xmlString || typeof xmlString !== 'string') return out
      try {
        const doc = new DOMParser().parseFromString(String(xmlString).replace(/\\n/g, '\n').trim(), 'text/xml')
        const addressesEl = doc.querySelector('Addresses')
        if (!addressesEl) return out
        const read = (tag) => {
          const el = addressesEl.querySelector(tag)
          if (!el || !el.getAttribute) return null
          const attr = (name) => (el.getAttribute(name) || '').trim()
          const struct = {
            placeId: attr('placeId'),
            postalCode: attr('postalCode'),
            city: attr('city'),
            county: attr('county'),
            countyCode: attr('countyCode'),
            street: attr('street'),
            number: attr('number'),
            details: attr('details')
          }
          const hasContent = !!(struct.placeId || struct.postalCode || struct.city || struct.street || struct.number)
          return hasContent ? struct : null
        }
        out.billing = read('Billing')
        out.delivery = read('Delivery')
      } catch (e) {
        console.warn('extractAddressStructsFromXmlMacdo:', e)
      }
      return out
    },

    /** Parse WoodOrder XML and return { billing, delivery } as address objects (or empty objects). */
    parseAddressesFromXmlMacdo (xmlString) {
      const empty = { billing: {}, delivery: {} }
      if (!xmlString || typeof xmlString !== 'string') return empty
      // Heuristique locale (dernier mot = localité) : suffisante pour un simple
      // AFFICHAGE. Pour créer une adresse en base, passer par
      // loadXmlMacdoAddressHints() qui interroge l'API.
      const toAddressObj = (line) => {
        if (!line) return {}
        const parts = line.split(/\s+/)
        const place = parts.length > 1 ? parts[parts.length - 1] : ''
        const street = parts.length > 1 ? parts.slice(0, -1).join(' ') : line
        return {
          street: street || '',
          nr: '',
          cp: '',
          place: place || '',
          region: '',
          country: 'Romania',
          province: '',
        }
      }
      // Champs séparés disponibles (borne à jour) : on les utilise TELS QUELS.
      // L'heuristique « dernier mot = localité » ci-dessus n'a plus lieu d'être —
      // c'est elle qui affichait « Dolj » comme localité pour
      // « Bechet Grivitei 17 Dolj ». Bonus : `region_code` alimente directement le
      // CountrySubentity e-factura sans passer par une table de correspondance.
      const fromStruct = (s) => {
        if (!s) return null
        return {
          street: s.street || '',
          nr: [s.number, s.details].filter(Boolean).join(' '),
          cp: s.postalCode || '',
          place: s.city || '',
          region: s.county || '',
          region_code: s.countyCode || '',
          country: 'Romania',
          province: ''
        }
      }
      const texts = this.extractAddressTextsFromXmlMacdo(xmlString)
      const structs = this.extractAddressStructsFromXmlMacdo(xmlString)
      return {
        billing: fromStruct(structs.billing) || toAddressObj(texts.billing),
        delivery: fromStruct(structs.delivery) || toAddressObj(texts.delivery)
      }
    },

    /** Parse WoodOrder XML and set addressbilling/addressdelivery from Addresses.Billing and Addresses.Delivery */
    applyAddressesFromXmlMacdo (xmlString) {
      const { billing, delivery } = this.parseAddressesFromXmlMacdo(xmlString)
      this.$set(this, 'addressbilling', billing)
      this.$set(this, 'addressdelivery', delivery)
    },

    /** Fallback: only set the side(s) that were not found in DB. */
    applyAddressesFromXmlMacdoFallback (xmlString, { fillBilling, fillDelivery }) {
      if (!fillBilling && !fillDelivery) return
      const { billing, delivery } = this.parseAddressesFromXmlMacdo(xmlString)
      if (fillBilling) this.$set(this, 'addressbilling', billing)
      if (fillDelivery) this.$set(this, 'addressdelivery', delivery)
    },

    /**
     * Interprète les deux lignes du XML côté serveur (même parseur que l'import).
     * Renvoie { billing, delivery } où chaque côté vaut null ou
     * { raw, street, number, postal, judet, place, score, ambiguous }.
     * `place` est null quand la localité n'a pas pu être reconnue : dans ce cas
     * aucune adresse ne doit être créée (elle serait refusée à l'envoi e-factura).
     */
    async loadXmlMacdoAddressHints (xmlString) {
      const texts = this.extractAddressTextsFromXmlMacdo(xmlString)
      const parseOne = async (text) => {
        if (!text) return null
        try {
          const { data } = await axios.post('/address/parse-ro', { text })
          return data
        } catch (e) {
          console.warn('parse-ro:', e)
          return null
        }
      }
      const [billing, delivery] = await Promise.all([parseOne(texts.billing), parseOne(texts.delivery)])
      return { billing, delivery }
    },
  },
}
